import { Location } from '@angular/common';
import { Component, OnInit } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import * as fromRoot from '@app/reducers';
import { Column } from '@app/shared/modules/smash-data-table/smash-data-table/smash-data-table.component';
import { isInThePast } from '@app/shared/helpers/helpers';
import { DialogService } from '@ngneat/dialog';
import { select, Store } from '@ngrx/store';
import { Observable } from 'rxjs';
import { first, map } from 'rxjs/operators';
import { Constant } from 'src/constant';
import { TransfersActions } from '../actions';
import { ModalDeleteTransferComponent } from './modals/modal-delete-transfer/modal-delete-transfer.component';
import { ModalEventListComponent } from './modals/modal-event-list/modal-event-list.component';
import * as fromTransferDetails from './reducers/transfer-details.reducer';

@Component({
  selector: 'app-transfer-details',
  templateUrl: './transfer-details.component.html',
  styleUrls: ['./transfer-details.component.scss']
})
export class TransferDetailsComponent implements OnInit {

  transferDetails$: Observable<any>;
  transferDetailsLoading$: Observable<boolean>;
  transferDetailsFiles$: Observable<any[]>;
  transferDetailsFilesLoading$: Observable<boolean>;
  transferEvents$: Observable<any[]>;
  transferEventsGroupedByEmail$: Observable<any[]>;
  transferEventsGroupedByAnonymousUser$: Observable<any[]>;
  transferEventsLoading$: Observable<boolean>;
  teamSelected$: Observable<any>;

  transfer: any;
  transferLoading = true;
  files: any;
  filesLoading = true;
  searchFiles: any;
  searchReceiver: any;
  searchEvents: any;
  selectedIdentity: any;

  type = {
    Link: 'PREMIUM_TRANSFER_SENT_DETAILS_TRANSFER_TYPE_LINK',
    Email: 'PREMIUM_TRANSFER_SENT_DETAILS_TRANSFER_TYPE_EMAIL',
    Slack: 'PREMIUM_TRANSFER_SENT_DETAILS_TRANSFER_TYPE_SLACK',
    Dropbox: 'PREMIUM_TRANSFER_SENT_DETAILS_TRANSFER_TYPE_DROPBOX',
    Outlook: 'PREMIUM_TRANSFER_SENT_DETAILS_TRANSFER_TYPE_OUTLOOK',
  };

  status = Constant.transferStatusTranslations;

  columnsEnabledFiles: Column[] = [
    { id: 'name', colName: 'PREMIUM_TABLE_FILE_LIST_LABEL_NAME', type: 'string', size: 45, searchable: true },
    { id: 'size', colName: 'PREMIUM_TABLE_FILE_LIST_LABEL_SIZE', type: 'number', pipe: 'size' },
  ];

  columnsEnabledEvents: Column[] = [
    { id: 'accessDate', colName: 'Date', type: 'date', pipe: 'date' },
    { id: 'userId', colName: 'User', type: 'string' },
    {
      id: 'type', colName: 'PREMIUM_TRANSFER_TABLE_LABEL_STATUS', type: 'string',
      translations: {
        View: 'PREMIUM_TRANSFER_SENT_DETAILS_EVENT_TABLE_LABEL_HAS_OPENED_TRANSFER',
        Download: 'PREMIUM_TRANSFER_SENT_DETAILS_EVENT_TABLE_LABEL_HAS_DOWNLOADED_TRANSFER',
      }
    },
  ];

  columnsEnabledReceivers: Column[] = [
    { id: 'email', colName: 'PREMIUM_TRANSFER_SENT_DETAILS_TABLE_RECEIVERS_LABEL_EMAIL', type: 'string', size: 45, searchable: true },
  ];

  constructor(
    private _location: Location,
    private store$: Store<fromRoot.State & fromTransferDetails.State>,
    private route: ActivatedRoute,
    public dialog: DialogService,
  ) {

    this.route.params
      .pipe(map(params => TransfersActions.loadTransfer({ transferId: params.transferId }))).subscribe(action => {
        this.store$.dispatch(action);
      });

    this.transferDetails$ = this.store$.pipe(select(fromTransferDetails.getTransferDetails));
    this.transferDetailsFilesLoading$ = this.store$.pipe(select(fromTransferDetails.getTransfersDetailsFilesLoading));
    this.transferDetailsLoading$ = this.store$.pipe(select(fromTransferDetails.getTransfersDetailsLoading));
    this.transferDetailsFiles$ = this.store$.pipe(select(fromTransferDetails.getTransferDetailsFiles));
    this.transferEvents$ = this.store$.pipe(select(fromTransferDetails.getTransferEvents));
    this.transferEventsGroupedByEmail$ = this.store$.pipe(select(fromTransferDetails.getTransferEventsGroupedByEmail));
    this.transferEventsGroupedByAnonymousUser$ = this.store$.pipe(select(fromTransferDetails.getTransferEventsGroupedByAnonymousUser));
    this.transferEventsLoading$ = this.store$.pipe(select(fromTransferDetails.getTransferEventsLoading));
    this.teamSelected$ = this.store$.pipe(select(fromRoot.getActiveIdentity));
  }

  ngOnInit() {
    this.transferDetailsLoading$.subscribe(res => {
      this.transferLoading = res;
    });

    this.transferDetailsFilesLoading$.subscribe(res => {
      this.filesLoading = res;
    });

    this.transferDetails$.subscribe(res => {
      this.transfer = res;
    });
    this.transferDetailsFiles$.subscribe(res => {
      this.files = res;
    });
  }

  async deleteTransfer() {
    const transfer = await this.transferDetails$.pipe(first(transfer => !!transfer)).toPromise();
    this.dialog.open(ModalDeleteTransferComponent, {
      closeButton: false,
      enableClose: true,
      size: 'sm',
      data: {
        transfer,
      }
    });
  }

  get transferExpired() {
    return isInThePast(this.transfer.availabilityEndDate);
  }

  get transferDeleted() {
    return this.transfer.status === Constant.transferStatus.Deleted;
  }

  // While the transfer is still being created, there is no link to show yet (and no
  // availabilityEndDate to compute an expiration countdown from) -- the "Lien"/"Durée de
  // validité" rows swap to "Statut"/the configured duration until upload completes.
  get isCreating() {
    return this.transfer?.status === Constant.transferStatus.Uploading;
  }

  // transfer.availabilityDuration's unit is inconsistent depending on the transfer's origin --
  // a standard Link/Email transfer reports it in DAYS, a dropbox-sourced one in SECONDS (same
  // units mismatch already known and tested for the dropbox settings form, see
  // dropbox-availability-duration.test.ts). Mirrors the existing convertAvailabilityDurationToDays
  // heuristic (shared/helpers/helpers.ts): a duration expressed in seconds is always >= 4 digits
  // in practice (the smallest configurable duration is 1 day = 86400 seconds), so a duration this
  // small in days would never naturally reach 4 digits.
  get availabilityDurationInDays(): number {
    const availabilityDuration = this.transfer?.availabilityDuration;
    const isSeconds = availabilityDuration?.toString().length > 3;
    return isSeconds ? availabilityDuration / Constant.oneDayInSecond : availabilityDuration;
  }

  refreshDetails() {
    this.route.params
      .pipe(map(params => TransfersActions.loadTransfer({ transferId: params.transferId }))).subscribe(action => {
        this.store$.dispatch(action);
      });
  }

  async openModalAnonymousUserTransferActivity(userId) {
    const events = await this.transferEvents$.pipe(first(events => !!events)).toPromise();
    this.dialog.open(ModalEventListComponent, {
      closeButton: false,
      enableClose: true,
      size: 'lg',
      data: {
        selectedIdentity: { userId },
        events,
      }
    });
  }

  async openModalUserTransferActivity(email) {
    const events = await this.transferEvents$.pipe(first(events => !!events)).toPromise();
    this.dialog.open(ModalEventListComponent, {
      closeButton: false,
      enableClose: true,
      size: 'lg',
      data: {
        selectedIdentity: { email },
        events,
      }
    });
  }

  back() {
    this._location.back();
  }
}
